/**
 * O erro que acontece ANTES de existir tela.
 *
 * G7, §20: a lista de estados obrigatórios inclui `error`. Ela vale para as
 * consultas — e vale, com mais razão, para a falha que impede o console de
 * subir.
 *
 * O QUE ISTO CORRIGE
 *
 *   `readSupabaseConfig` recusa configuração errada lançando, o que está certo:
 *   um cliente construído com URL de outro projeto só falharia na primeira
 *   consulta, com "não autorizado", e mandaria quem lê procurar problema de RLS.
 *
 *   Sem uma fronteira que apare esse lançamento, porém, o React desmonta a
 *   árvore inteira e a página fica BRANCA. Trocamos um erro tardio e confuso por
 *   um erro precoce e invisível — e "a tela não carrega" é a pior mensagem de
 *   diagnóstico que existe.
 *
 *   A fronteira devolve o `error` do §20 ao lugar onde ele faltava.
 *
 * POR QUE ELA ENVOLVE A SESSÃO INTEIRA
 *
 *   O cliente Supabase é construído na primeira renderização de
 *   `SessionProvider`. Uma fronteira colocada mais para dentro nunca seria
 *   montada — o erro acontece acima dela.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  readonly children: ReactNode;
}

interface State {
  readonly erro: Error | null;
}

export class BootErrorBoundary extends Component<Props, State> {
  override state: State = { erro: null };

  static getDerivedStateFromError(erro: Error): State {
    return { erro };
  }

  override componentDidCatch(erro: Error, info: ErrorInfo): void {
    /*
     * O console do navegador recebe o erro e a pilha de componentes.
     *
     * Nada de segredo passa por aqui: a mensagem de `SupabaseConfigError` fala
     * de NOMES de variáveis e do ref do projeto, nunca de valores de chave — e
     * o `readSupabaseConfig` foi escrito assim de propósito.
     */
    console.error('[OpteFiscal] falha na inicialização do console', erro, info.componentStack);
  }

  override render(): ReactNode {
    const { erro } = this.state;
    if (erro === null) return this.props.children;

    return (
      <div className="of-standalone">
        <div className="of-panel-state of-panel-state--error" role="alert">
          <strong className="of-panel-state__title">O console não pôde iniciar</strong>
          <p className="of-panel-state__detail">
            Isto não é falha de login nem de permissão: o aplicativo parou antes de conseguir
            perguntar qualquer coisa ao servidor. Normalmente é configuração de ambiente.
          </p>
          <code className="of-panel-state__code">{erro.message}</code>
        </div>
      </div>
    );
  }
}
